/* eslint-disable no-invalid-this */
/* eslint-disable array-element-newline */
/* eslint-disable no-unused-vars */
import {makeObservable, action, observable, reaction} from 'mobx';
import StorageFactory from '@mvps-genesis/storage-factory';
import {savePreviousUrl} from '../services/middleware';
import {SearchService} from 'extensions/hawksearch';
import _merge from 'lodash.merge';

const STORAGE_KEY_PREFIX = 'borsheims-rb-';

/**
 * @typedef {Object} MivaProductAttribute
 * @prop {string} code
 * @prop {string|number} data
 */

/**
 * @typedef {Object.<string, (any) => import('mobx').IReactionDisposer>} Reactions
 */

export class ProductStore {
	_parentStore;

	/**
	 * @type {import('extensions/hawksearch/services/types').SearchResultsItem}
	 */
	product = {};

	/**
	 * @type {string}
	 */
	type = 'product';

	/**
	 * @type {import('mobx').IReactionDisposer[]}
	 */
	disposers = [];

	/**
	 * @type {import('extensions/hawksearch/services/types').SearchResultsItem[]}
	 */
	compareList = [];

	/**
	 * @type {boolean}
	 */
	compareListActive = false;

	/**
	 * @type {string}
	 */
	previousUrl = '';

	/**
	 * @type {import('extensions/hawksearch/services/types').SearchServiceOptions}
	 */
	searchServiceOptions = {
		allowRedirects: false,
		middleware: {
			afterStore: [savePreviousUrl(this)]
		}
	};

	/**
	 * @type {SearchService}
	 */
	searchService;

	/**
	 * @type {import('extensions/hawksearch/services/types').SearchRequest}
	 */
	get initialProductLoaderFacets () {
		return void 0;
	}

	/**
	 * @type {Reactions}
	 */
	reactions = {
		storageState: () => {
			this.setStorageState();
		}
	};

	parseProductStorageState (product) {
		return {
			id: product
		};
	}

	getStorageState () {
		const storageState = StorageFactory.get(`${STORAGE_KEY_PREFIX}-${this.type}`);
		this.setCompareList(storageState?.compareList || this.compareList || []);
		this.setPreviousUrl(storageState?.previousUrl);
	}

	setStorageState () {
		StorageFactory.set(`${STORAGE_KEY_PREFIX}-${this.type}`, this.storageState);
	}

	mergeState (storageState) {
		this.product = this.parseProductStorageState(storageState?.product);
		this.setCompareList(storageState?.compareList || this.compareList || []);

		return this;
	}

	get storageState () {
		return {
			compareList: this.compareList?.map(compareProduct => compareProduct?.id) || [],
			previousUrl: this.previousUrl
				? this.previousUrl
				: ''
		};
	}

	constructor (parentStore) {
		this._parentStore = parentStore;

		makeObservable(this, {
			addProductToCompareList: action,
			compareList: observable.deep,
			compareListActive: observable,
			previousUrl: observable,
			product: observable,
			removeProductFromCompareList: action,
			setCompareList: action,
			setCompareListActive: action,
			setPreviousUrl: action,
			setProduct: action,
			toggleCompareListProduct: action
		});
	}

	async init () {
		this.getStorageState();
		await this.loadProductRecordsFromStorage();
		this.activateReactions();

		return this;
	}

	handleSelectedProducts (results = []) {
		// Handle selected product
		const selectedProduct = results.find(result => result.id === this.product.id);
		if (typeof selectedProduct === 'undefined' && this.product.id !== '') {

			// Push error message if selectedProduct is not found and the product.id state value is set
			this._parentStore.messagesStore.add({
				text: `Apologies, the ${this.type} you selected is no longer available.`,
				type: 'error'
			});
		}

		this.setProduct(selectedProduct || {});
	}

	handleCompareList (results = []) {
		// Handle compare products
		const newCompareList = results.filter(result => this.compareList.indexOf(result.id) !== -1);

		// Output message if compare list changed
		const diff = this.compareList.length - newCompareList.length;
		if (diff > 0) {
			this._parentStore.messagesStore.add({
				text: `Unable to load ${diff > 1 ? 'products' : 'a product'} previously saved in your ${this.type} compare list. The affected ${diff > 1 ? 'products have' : 'product has'} been removed.`,
				type: 'error'
			});
		}

		// Set new compare list and save to storage
		this.setCompareList(newCompareList);
		this.setStorageState();
	}

	// eslint-disable-next-line max-lines-per-function
	async loadProductRecordsFromStorage () {
		// Execute search if facets exist
		if (this.initialProductLoaderFacets) {
			const initialSearchService = new SearchService(_merge({}, this.searchServiceOptions, {
				...this.initialProductLoaderFacets,
				clientOnly: true
			}));

			// Run search query
			await initialSearchService.search({
				facets: this.initialProductLoaderFacets?.facets
			});

			// Handle the products returned from the results
			this.handleSelectedProducts(initialSearchService?.store?.state?.results);
			this.handleCompareList(initialSearchService?.store?.state?.results);
		}
	}

	clear () {
		this.mergeStorageState({});
	}

	setProduct (product) {
		this.product = product;
		return this;
	}

	setCompareList (list = []) {
		this.compareList = list?.filter(compareProduct => compareProduct !== null && compareProduct !== void 0);
		return this;
	}

	addProductToCompareList (product) {
		this.compareList.push(product);
		return this;
	}

	removeProductFromCompareList (product) {
		this.compareList = this.compareList.filter(compareProduct => compareProduct?.id !== product.id);
		return this;
	}

	toggleCompareListProduct (product) {
		const found = this.compareList.find(compareProduct => compareProduct?.id === product.id);
		return found
			? this.removeProductFromCompareList(product)
			: this.addProductToCompareList(product);
	}

	setCompareListActive (bool) {
		this.compareListActive = Boolean(bool);
		return this;
	}

	setPreviousUrl (previousUrl) {
		this.previousUrl = previousUrl;
	}

	activateReactions () {
		this.disposers = Object.keys(this.reactions).map(key => {
			return reaction(
				() => this[key],
				this.reactions[key]
			);
		});
	}

	deactivateReactions () {
		for (let disposer of this.disposers) {
			disposer();
		}
	}

	createSearchService () {
		return new SearchService(this.searchServiceOptions);
	}

	applyBackgroundFacets (facets) {
		this.searchService.backgroundFacets = _merge({}, this.searchService.backgroundFacets, facets);
	}

	compatabilityParams () {
		return void 0;
	}

	/**
	 * @param {import('extensions/hawksearch/services/types').SearchResultsItem} product
	 */
	applyCompatabilityParams (product) {
		const compatabilityParams = this.compatabilityParams(product);

		if (compatabilityParams.backgroundFacets) {
			this.applyBackgroundFacets(compatabilityParams.backgroundFacets);
		}
		else {
			this.searchService.defaultRequestParams = structuredClone(this.compatabilityParams(product));
		}
	}

};

